/**
 * Fury Lab: derive blocks from time slots and manage assignment helpers.
 */

/** Format event date for display (e.g. "March 21, 2026"). */
export function formatEventDate(dateVal) {
  if (dateVal == null || String(dateVal).trim() === '') return null;
  let d;
  if (dateVal && typeof dateVal.toDate === 'function') {
    d = dateVal.toDate();
  } else if (dateVal instanceof Date) {
    d = dateVal;
  } else {
    const s = String(dateVal).trim();
    d = new Date(s);
  }
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Generate a stable block id from slot index and block index within slot.
 * @param {number} slotIndex
 * @param {number} blockIndexInSlot
 * @returns {string}
 */
export function blockId(slotIndex, blockIndexInSlot) {
  return `slot-${slotIndex}-block-${blockIndexInSlot}`;
}

/**
 * Build or rebuild the blocks array from timeSlots.
 *
 * - One logical block per time slot (slotIndex).
 * - Each block supports any number of coaches via `coaches: string[]` (coach ids).
 * - For backward-compatibility, we continue to expose `coachId` / `coachName`:
 *   - `coachId` is the first coach id in the `coaches` array (or '' if none).
 *   - `coachName` is a stored display string for the coach(es) for that block.
 * - Per‑participant coach assignment within a block can be stored in
 *   `studentAssignments: { [participantId: string]: string }` where the value is the coachId.
 *
 * Multiple participants are assigned to that same block via the furyLabAssignments collection.
 * This helper only shapes the blocks config and preserves existing fields when possible.
 *
 * @param {Array<{ start: string, end: string }>} timeSlots
 * @param {Array<{
 *   id: string,
 *   slotIndex: number,
 *   blockIndexInSlot: number,
 *   coachId?: string,
 *   coachName?: string,
 *   coaches?: string[],
 *   studentAssignments?: { [participantId: string]: string },
 * }>} existingBlocks
 * @returns {Array<{
 *   id: string,
 *   slotIndex: number,
 *   blockIndexInSlot: number,
 *   coachId: string,
 *   coachName: string,
 *   coaches: string[],
 *   studentAssignments: { [participantId: string]: string },
 * }>}
 */
export function deriveBlocksFromTimeSlots(timeSlots, existingBlocks = []) {
  const byId = new Map();
  (existingBlocks || []).forEach((b) => byId.set(b.id, b));

  const result = [];
  (timeSlots || []).forEach((slot, slotIndex) => {
    const id = blockId(slotIndex, 0);
    const existing = byId.get(id);
    const existingCoaches = Array.isArray(existing?.coaches)
      ? existing.coaches.filter((c) => typeof c === 'string' && c)
      : [];
    const legacyCoachId = existing?.coachId || '';
    const coaches =
      existingCoaches.length > 0
        ? existingCoaches
        : legacyCoachId
        ? [legacyCoachId]
        : [];
    const studentAssignments =
      existing && existing.studentAssignments && typeof existing.studentAssignments === 'object'
        ? existing.studentAssignments
        : {};
    result.push({
      id,
      slotIndex,
      blockIndexInSlot: 0,
      coachId: coaches[0] || '',
      coachName:
        existing && existing.coachName != null ? String(existing.coachName) : '',
      coaches,
      studentAssignments,
    });
  });
  return result;
}

/**
 * Get participant id for assignment doc id (eventId + participantId).
 * @param {string} eventId
 * @param {string} participantId
 * @returns {string}
 */
export function assignmentDocId(eventId, participantId) {
  return `${eventId}_${participantId}`;
}

function add30Minutes(timeStr) {
  if (!timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) return '09:30';
  const [h, m] = timeStr.split(':').map((n) => parseInt(n, 10));
  let total = (h || 0) * 60 + (m || 0);
  total += 30;
  const outH = Math.floor(total / 60) % 24;
  const outM = total % 60;
  return `${outH.toString().padStart(2, '0')}:${outM.toString().padStart(2, '0')}`;
}

/**
 * Human-readable lines for assigned blocks (same shape as Fury Lab email merge).
 */
export function buildBlockSummary(blockIds, blocks, timeSlots) {
  if (!blockIds || !blockIds.length || !blocks || !timeSlots) return '';
  const blockMap = new Map((blocks || []).map((b) => [b.id, b]));
  const slotMap = (timeSlots || []).map((s) => ({ start: s.start, end: s.end }));
  const parts = blockIds
    .map((bid) => {
      const block = blockMap.get(bid);
      if (!block) return null;
      const slot = slotMap[block.slotIndex];
      const timeLabel = slot ? `${slot.start}–${slot.end}` : '';
      const coach = block.coachName ? ` with ${block.coachName}` : '';
      return `• ${timeLabel}${coach}`;
    })
    .filter(Boolean);
  return parts.join('\n');
}

/**
 * Participant list is exactly current Fury Lab ticket holders (flyer ids). Preserves meet times
 * from the saved row when the flyer still has a ticket.
 *
 * @param {Array<{ id: string, name?: string, email?: string, meetTime?: string }>} ticketHolders
 * @param {Array<{ id: string, name?: string, email?: string, meetTime?: string }>} savedParticipants
 * @returns {Array<{ id: string, name?: string, email?: string, meetTime?: string }>}
 */
export function mergeFuryLabParticipantsFromTickets(ticketHolders, savedParticipants) {
  const prevById = new Map((savedParticipants || []).map((p) => [p.id, p]));
  const seen = new Set();
  return (ticketHolders || [])
    .map((p) => ({
      ...p,
      meetTime: prevById.get(p.id)?.meetTime ?? '',
    }))
    .filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
}

/** Compare participant lists for sync (order-insensitive). */
export function furyLabParticipantsListsEqual(a, b) {
  const norm = (list) =>
    [...(list || [])]
      .map((p) => ({
        id: p.id,
        name: (p.name || '').trim(),
        email: (p.email || '').trim(),
        meetTime: p.meetTime != null ? String(p.meetTime).trim() : '',
      }))
      .sort((x, y) => String(x.id).localeCompare(String(y.id)));
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

/**
 * Normalize a `furyLabConfig` document for merge helpers (matches Fury Lab screen).
 * @returns {{ timeSlots: Array, blocks: Array, participants: Array } | null}
 */
export function normalizeFuryLabConfigDoc(data) {
  if (!data || typeof data !== 'object') return null;
  const slots = (Array.isArray(data.timeSlots) ? data.timeSlots : []).map((s) => ({
    ...s,
    end: s.end || add30Minutes(s.start),
    flyers: Math.max(1, Math.min(12, Number(s.flyers) || 2)),
  }));
  const participants = (Array.isArray(data.participants) ? data.participants : []).map((p) => ({
    ...p,
    meetTime: p.meetTime != null ? p.meetTime : '',
  }));
  const blocks = deriveBlocksFromTimeSlots(slots, data.blocks || []);
  return { timeSlots: slots, blocks, participants };
}

/**
 * Attach Fury Lab meet time, block summary, and coach (from config + assignments) onto a flyer row.
 * Participant id matches flyer document id.
 *
 * @param {object} flyer
 * @param {{ timeSlots: Array, blocks: Array, participants: Array, assignments: Array }} bundle
 */
export function mergeFuryLabIntoFlyerForEmail(flyer, bundle) {
  if (!flyer || !flyer.id || !bundle) return flyer;
  const { blocks, timeSlots, participants, assignments = [] } = bundle;
  const pid = flyer.id;
  const participant = (participants || []).find((p) => p.id === pid);
  const assignment = assignments.find((a) => a.participantId === pid);
  const meetTime =
    participant && participant.meetTime != null && String(participant.meetTime).trim()
      ? String(participant.meetTime).trim()
      : '';
  const blockSummaryStr =
    assignment && assignment.blockIds && assignment.blockIds.length
      ? buildBlockSummary(assignment.blockIds, blocks, timeSlots)
      : '';
  const firstBlockId =
    assignment && assignment.blockIds && assignment.blockIds.length ? assignment.blockIds[0] : null;
  const firstBlock = firstBlockId ? (blocks || []).find((b) => b.id === firstBlockId) : null;
  const coachNameStr = firstBlock && firstBlock.coachName ? String(firstBlock.coachName).trim() : '';
  return {
    ...flyer,
    meetTime: meetTime || (flyer.meetTime != null ? String(flyer.meetTime).trim() : ''),
    blockSummary: blockSummaryStr || (flyer.blockSummary != null ? String(flyer.blockSummary) : ''),
    coachName: coachNameStr || (flyer.coachName != null ? String(flyer.coachName).trim() : ''),
  };
}
